import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import AdminEventForm from './AdminEventForm';
import DocumentsSection from './DocumentsSection';
import SlideshowEditor from './SlideshowEditor';
import AgendasMinutesPage from './AgendasMinutesPage';
import CalendarPage from './CalendarPage';
import VillageOfficials from './VillageOfficials';
import './App.css';
import './NavFix.css';

// Use relative paths - the API is served from the same origin
const API_BASE = '';

function App() {
  const [currentView, setCurrentView] = useState(() => {
    // Restore the last viewed page, default to 'home'
    return localStorage.getItem('currentView') || 'home';
  });
  const [agendas, setAgendas] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [homeContent, setHomeContent] = useState({
    title: "",
    content: ""
  });
  const [weather, setWeather] = useState({
    temp_F: "--",
    condition: "Loading..."
  });
  const [isAdmin, setIsAdmin] = useState(() => {
    return localStorage.getItem('isAdmin') === 'true';
  });
  const [authToken, setAuthToken] = useState(() => {
    return localStorage.getItem('authToken');
  });
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showSlideshowEditor, setShowSlideshowEditor] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [footerContent, setFooterContent] = useState({
    column1: '',
    column2: '',
    column3: ''
  });
  const [sidebarContent, setSidebarContent] = useState({
    title: '',
    content: ''
  });
  const [showWeather, setShowWeather] = useState(false);
  const [showCalendar, setShowCalendar] = useState(() => {
    // Check localStorage for saved preference, default to calendar view
    const saved = localStorage.getItem('eventsViewPreference');
    return saved ? saved === 'calendar' : true;
  });
  const [showAgendaForm, setShowAgendaForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  
  // Get weather icon class for Weather Icons font (professional flat icons)
  const getWeatherIcon = (condition) => {
    const cond = (condition || '').toLowerCase();
    
    // Weather Icons font classes - clean flat design icons
    if (cond.includes('sun') || cond.includes('clear')) return 'wi-day-sunny';
    else if (cond.includes('partly')) return 'wi-day-cloudy';
    else if (cond.includes('cloud') || cond.includes('overcast')) return 'wi-cloudy';
    else if (cond.includes('rain') || cond.includes('shower')) return 'wi-rain';
    else if (cond.includes('drizzle')) return 'wi-sprinkle';
    else if (cond.includes('storm') || cond.includes('thunder')) return 'wi-thunderstorm';
    else if (cond.includes('snow')) return 'wi-snow';
    else if (cond.includes('sleet')) return 'wi-sleet';
    else if (cond.includes('fog') || cond.includes('mist')) return 'wi-fog';
    else if (cond.includes('haz')) return 'wi-day-haze';
    else if (cond.includes('wind')) return 'wi-windy';
    
    return 'wi-cloud'; // Default
  };
  
  // Save currentView to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('currentView', currentView);
  }, [currentView]);

  // Load initial data
  useEffect(() => {
    loadWeather();
    loadContent();
    loadAgendas();
    loadPhotos();
    loadFooter();
    loadSidebar();
    loadSettings();
    
    // Refresh weather every 5 minutes
    const weatherInterval = setInterval(loadWeather, 5 * 60 * 1000);
    
    // Slideshow timer
    const slideInterval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % Math.max(1, photos.length));
    }, 5000);
    
    return () => {
      clearInterval(weatherInterval);
      clearInterval(slideInterval);
    };
  }, [photos.length]);
  
  const loadWeather = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/weather`);
      // Extract current weather data
      if (response.data && response.data.current) {
        setWeather({
          temp_F: response.data.current.temp_F,
          condition: response.data.current.condition
        });
      }
    } catch (error) {
      console.error('Failed to load weather:', error);
      // Keep showing -- if weather unavailable
      setWeather({
        temp_F: "--",
        condition: "Unavailable"
      });
    }
  };
  
  const loadContent = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/content`);
      setHomeContent(response.data);
    } catch (error) {
      console.error('Failed to load content:', error);
    }
  };

  const loadFooter = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/footer`);
      if (response.data) {
        // If it has proper new format content (not just "###"), use it
        if (response.data.column1 && response.data.column1 !== "###" && response.data.column2 && response.data.column3) {
          setFooterContent(response.data);
        } 
        // Otherwise convert old format to new - just combine the fields, no extra formatting
        else if (response.data.address) {
          const converted = {
            column1: `${response.data.address}\n\nPhone: ${response.data.phone}\nEmail: ${response.data.email}`,
            column2: `${response.data.hours}\n\n${response.data.boardMeetings}`,
            column3: response.data.about
          };
          setFooterContent(converted);
          // Don't auto-save - let admin do it manually when they want
        }
      }
    } catch (error) {
      console.error('Failed to load footer:', error);
    }
  };

  const loadSidebar = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/sidebar`);
      if (response.data) {
        setSidebarContent(response.data);
      }
    } catch (error) {
      console.error('Failed to load sidebar:', error);
      // Use default content if load fails
    }
  };

  const handleFooterUpdate = async (column, content) => {
    const updatedFooter = {...footerContent, [column]: content};
    setFooterContent(updatedFooter);
    try {
      const formData = new FormData();
      formData.append('column1', updatedFooter.column1 || '');
      formData.append('column2', updatedFooter.column2 || '');
      formData.append('column3', updatedFooter.column3 || '');
      
      await axios.post(`${API_BASE}/api/admin/footer`, formData, {
        headers: { 'Authorization': `Basic ${authToken}` }
      });
    } catch (error) {
      console.error('Failed to update footer:', error);
      alert('Failed to save footer changes');
    }
  };

  const handleSidebarUpdate = async (updatedContent) => {
    setSidebarContent(updatedContent);
    try {
      const formData = new FormData();
      formData.append('title', updatedContent.title || 'Quick Links');
      formData.append('content', updatedContent.content || '');
      
      await axios.post(`${API_BASE}/api/admin/sidebar`, formData, {
        headers: { 'Authorization': `Basic ${authToken}` }
      });
    } catch (error) {
      console.error('Failed to update sidebar:', error);
      alert('Failed to save sidebar changes');
    }
  };
  
  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/settings`);
      setShowWeather(response.data.show_weather_widget === 'true' || response.data.show_weather_widget === true);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadAgendas = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/events`);
      setAgendas(response.data);
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };
  
  const loadPhotos = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/photos`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Failed to load photos:', error);
    }
  };
  
  const handleLogin = async (username, password) => {
    try {
      const token = btoa(`${username}:${password}`);
      console.log('Attempting login with token:', token.substring(0, 10) + '...');
      const response = await axios.get(`${API_BASE}/api/admin/check`, {
        headers: { 'Authorization': `Basic ${token}` }
      });

      console.log('Login response:', response.status);
      if (response.status === 200) {
        setAuthToken(token);
        setIsAdmin(true);
        localStorage.setItem('authToken', token);
        localStorage.setItem('isAdmin', 'true');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error.response?.status, error.response?.data);
      alert('Invalid username or password. Please try again.');
      return false;
    }
  };
  
  const handleContentUpdate = async (newContent) => {
    try {
      const formData = new FormData();
      formData.append('title', newContent.title || '');
      formData.append('content', newContent.content || '');
      
      await axios.post(`${API_BASE}/api/admin/content`, formData, {
        headers: { 'Authorization': `Basic ${authToken}` }
      });
      setHomeContent(newContent);
      // No alert on auto-save, just silently save
    } catch (error) {
      console.error('Failed to update content:', error);
      alert('Failed to save changes. Please try again.');
    }
  };
  
  const handlePhotoUpload = async (file, caption) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caption', caption);
    formData.append('order', photos.length);
    
    try {
      await axios.post(`${API_BASE}/api/admin/photos`, formData, {
        headers: { 
          'Authorization': `Basic ${authToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      loadPhotos();
      alert('Photo uploaded successfully');
    } catch (error) {
      alert('Failed to upload photo');
    }
  };
  
  const handlePhotoDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/admin/photos/${photoId}`, {
        headers: { 'Authorization': `Basic ${authToken}` }
      });
      loadPhotos();
    } catch (error) {
      alert('Failed to delete photo');
    }
  };
  
  const handleAgendaCreate = async (agenda) => {
    try {
      await axios.post(`${API_BASE}/api/admin/agendas`, agenda, {
        headers: { 'Authorization': `Basic ${authToken}` }
      });
      loadAgendas();
      alert('Agenda created successfully');
    } catch (error) {
      alert('Failed to create agenda');
    }
  };
  
  const handleAgendaDelete = async (agendaId) => {
    if (!window.confirm('Delete this agenda?')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/admin/events/${agendaId}`, {
        headers: { 'Authorization': `Basic ${authToken}` }
      });
      loadAgendas();
    } catch (error) {
      alert('Failed to delete agenda');
    }
  };
  
  return (
    <div className="App">
      {/* Header Section - EXACT from original */}
      <header className="header-main">
        <div className="header-container">
          {/* Navigation */}
          <nav className="navbar">
            <ul className="navbar-nav">
              <li className="nav-item">
                <a 
                  className={`nav-link ${currentView === 'home' ? 'active' : ''}`}
                  href="#"
                  onClick={(e) => { e.preventDefault(); setCurrentView('home'); }}
                >
                  Home
                </a>
              </li>
              <li className="nav-item">
                <a 
                  className={`nav-link ${currentView === 'agendas' ? 'active' : ''}`}
                  href="#"
                  onClick={(e) => { e.preventDefault(); setCurrentView('agendas'); }}
                >
                  Agendas & Minutes
                </a>
              </li>
              <li className="nav-item">
                <a 
                  className={`nav-link ${currentView === 'calendar' ? 'active' : ''}`}
                  href="#"
                  onClick={(e) => { e.preventDefault(); setCurrentView('calendar'); }}
                >
                  Events
                </a>
              </li>
              <li className="nav-item">
                <a 
                  className={`nav-link ${currentView === 'documents' ? 'active' : ''}`}
                  href="#"
                  onClick={(e) => { e.preventDefault(); setCurrentView('documents'); }}
                >
                  Forms, Codes & Ordinances
                </a>
              </li>
              <li className="nav-item">
                <a 
                  className={`nav-link ${currentView === 'officials' ? 'active' : ''}`}
                  href="#"
                  onClick={(e) => { e.preventDefault(); setCurrentView('officials'); }}
                >
                  Village Officials
                </a>
              </li>
              {/* Weather Widget IN the navbar */}
              {showWeather ? (
                <li className="nav-weather">
                  <i className={`wi ${getWeatherIcon(weather.condition)}`} title={weather.condition}></i>
                  <span className="weather-temp">{weather.temp_F}°F</span>
                  {isAdmin && (
                    <button
                      className="weather-toggle-btn"
                      onClick={async () => {
                        setShowWeather(false);
                        try {
                          const formData = new FormData();
                          formData.append('show_weather_widget', 'false');
                          await axios.post(`${API_BASE}/api/admin/settings`, formData, {
                            headers: { 'Authorization': `Basic ${authToken}` }
                          });
                        } catch (error) {
                          console.error('Failed to save setting:', error);
                        }
                      }}
                      title="Hide weather widget"
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        color: 'white',
                        marginLeft: '8px',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      ×
                    </button>
                  )}
                </li>
              ) : (
                isAdmin && (
                  <li className="nav-weather">
                    <button
                      onClick={async () => {
                        setShowWeather(true);
                        try {
                          const formData = new FormData();
                          formData.append('show_weather_widget', 'true');
                          await axios.post(`${API_BASE}/api/admin/settings`, formData, {
                            headers: { 'Authorization': `Basic ${authToken}` }
                          });
                        } catch (error) {
                          console.error('Failed to save setting:', error);
                        }
                      }}
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Show Weather
                    </button>
                  </li>
                )
              )}
            </ul>
          </nav>
        </div>
      </header>
      
      {/* Homepage Intro Section with Village Badge - Only show on home */}
      {currentView === 'home' && (
        <section className="homepage-intro">
          <div className="container">
            <div className="city-badge">
              <a href="#" onClick={() => setCurrentView('home')}>
                <img 
                  width="250" 
                  height="250"
                  src="/village-seal.png"
                  className="img-responsive"
                  alt="Village of Thomasboro, Champaign County, IL"
                />
              </a>
            </div>
          </div>
        </section>
      )}
      
      {/* Main Content Area */}
      <div className="content-area">
        <div className="container">
          <div className="row">
            {/* Main Column - full width when not on home page */}
            <div className={currentView === 'home' ? 'col-md-8' : 'col-md-12'}>
              {currentView === 'home' ? (
                <>
                  {/* Welcome Text Block */}
                  <div className="fl-rich-text">
                    {isAdmin ? (
                      <MarkdownEditor
                        title={homeContent.title}
                        content={homeContent.content}
                        onTitleChange={(title) => setHomeContent({...homeContent, title})}
                        onContentChange={(content) => setHomeContent({...homeContent, content})}
                        onSave={(content) => handleContentUpdate({...homeContent, content: content ?? homeContent.content})}
                        authToken={authToken}
                      />
                    ) : (
                      <>
                        <h4>{homeContent?.title || "Welcome to Thomasboro"}</h4>
                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>{homeContent?.content || "Loading..."}</ReactMarkdown>
                      </>
                    )}
                  </div>
                  
                  {/* Photo Slideshow */}
                  <div className="fl-slideshow-container">
                    {photos && photos.length > 0 ? (
                      <div>
                        <div className="slideshow-wrapper">
                          {photos.map((photo, index) => (
                            <img
                              key={photo.id}
                              src={`${API_BASE}/uploads/${photo.filename}`}
                              alt={photo.caption || `Photo ${index + 1}`}
                              className={`slideshow-image ${index === currentSlide ? 'active' : ''}`}
                            />
                          ))}
                          <div className="slideshow-controls">
                            {photos.map((_, index) => (
                              <div
                                key={index}
                                className={`slideshow-dot ${index === currentSlide ? 'active' : ''}`}
                                onClick={() => setCurrentSlide(index)}
                              />
                            ))}
                          </div>
                        </div>
                        {photos[currentSlide]?.caption && (
                          <div className="slideshow-caption-bar">
                            {photos[currentSlide].caption}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p style={{textAlign: 'center', color: '#999', padding: '50px'}}>
                        No photos uploaded yet
                      </p>
                    )}
                    {isAdmin && (
                      <button 
                        className="btn btn-primary" 
                        style={{marginTop: '15px', display: 'block', margin: '15px auto'}}
                        onClick={() => setShowSlideshowEditor(true)}
                      >
                        Edit Slideshow
                      </button>
                    )}
                  </div>
                </>
              ) : currentView === 'agendas' ? (
                <AgendasMinutesPage isAdmin={isAdmin} authToken={authToken} />
              ) : currentView === 'calendar' ? (
                <CalendarPage isAdmin={isAdmin} authToken={authToken} />
              ) : currentView === 'documents' ? (
                <DocumentsSection isAdmin={isAdmin} authToken={authToken} />
              ) : currentView === 'officials' ? (
                <VillageOfficials isAdmin={isAdmin} authToken={authToken} />
              ) : currentView === 'privacy' ? (
                <PrivacyPolicy />
              ) : currentView === 'accessibility' ? (
                <AccessibilityStatement />
              ) : null}
            </div>
            
            {/* Sidebar - only show on home page */}
            {currentView === 'home' && (
              <div className="col-md-4">
                <div className="widget-area">
                  <aside className="widget">
                    {isAdmin ? (
                      <MarkdownEditor
                        title={sidebarContent.title}
                        content={sidebarContent.content}
                        onTitleChange={(title) => setSidebarContent({...sidebarContent, title})}
                        onContentChange={(content) => setSidebarContent({...sidebarContent, content})}
                        onSave={(content) => handleSidebarUpdate({...sidebarContent, content: content ?? sidebarContent.content})}
                        authToken={authToken}
                      />
                    ) : (
                      <>
                        <h3 className="widget-title">{sidebarContent.title}</h3>
                        <div className="sidebar-content">
                          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{sidebarContent.content}</ReactMarkdown>
                        </div>
                      </>
                    )}
                  </aside>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Footer - Editable in admin mode */}
      <footer className="footer-main">
        <div className="container">
          <div className="footer-row">
            <div className="footer-col">
              {isAdmin ? (
                <MarkdownEditor
                  content={footerContent.column1}
                  onTitleChange={() => {}}
                  onContentChange={(content) => setFooterContent({...footerContent, column1: content})}
                  onSave={(currentContent) => handleFooterUpdate('column1', currentContent ?? footerContent.column1)}
                  authToken={authToken}
                />
              ) : (
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>{footerContent.column1 || ''}</ReactMarkdown>
              )}
            </div>
            <div className="footer-col">
              {isAdmin ? (
                <MarkdownEditor
                  content={footerContent.column2}
                  onTitleChange={() => {}}
                  onContentChange={(content) => setFooterContent({...footerContent, column2: content})}
                  onSave={(currentContent) => handleFooterUpdate('column2', currentContent ?? footerContent.column2)}
                  authToken={authToken}
                />
              ) : (
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>{footerContent.column2 || ''}</ReactMarkdown>
              )}
            </div>
            <div className="footer-col">
              {isAdmin ? (
                <MarkdownEditor
                  content={footerContent.column3}
                  onTitleChange={() => {}}
                  onContentChange={(content) => setFooterContent({...footerContent, column3: content})}
                  onSave={(currentContent) => handleFooterUpdate('column3', currentContent ?? footerContent.column3)}
                  authToken={authToken}
                />
              ) : (
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>{footerContent.column3 || ''}</ReactMarkdown>
              )}
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Village of Thomasboro. All rights reserved. | 
              <a href="#" onClick={(e) => { e.preventDefault(); setCurrentView('privacy'); window.scrollTo(0, 0); }}> Privacy Policy</a> | 
              <a href="#" onClick={(e) => { e.preventDefault(); setCurrentView('accessibility'); window.scrollTo(0, 0); }}> Accessibility</a>
            </p>
          </div>
        </div>
      </footer>
      
      {/* Admin Login Button - Only show if not logged in */}
      {!isAdmin && (
        <div className="admin-panel">
          <button className="admin-btn" onClick={() => setShowAdminModal(true)}>
            Admin
          </button>
        </div>
      )}
      
      {/* Admin Login Modal */}
      {showAdminModal && !isAdmin && (
        <AdminLoginModal
          onLogin={handleLogin}
          onClose={() => setShowAdminModal(false)}
        />
      )}
      
      {/* Slideshow Editor Modal */}
      {showSlideshowEditor && isAdmin && (
        <SlideshowEditor
          photos={photos}
          authToken={authToken}
          onUpdate={() => {
            loadPhotos(); // Reload photos after any changes
          }}
          onClose={() => {
            setShowSlideshowEditor(false);
            loadPhotos(); // Reload photos after editing
          }}
        />
      )}
      
      {/* Admin Logout Button - Only show if logged in */}
      {isAdmin && (
        <div className="admin-panel" style={{bottom: '20px', right: '20px'}}>
          <button className="admin-btn logout-btn" onClick={() => {
            setIsAdmin(false);
            setAuthToken(null);
            localStorage.removeItem('authToken');
            localStorage.removeItem('isAdmin');
            alert('Logged out successfully');
          }}>
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

// List View Component with Month Filtering
function ListView({ agendas, isAdmin, onDelete, authToken, onUpdate }) {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [viewAll, setViewAll] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  // Sort agendas by date
  const sortedAgendas = [...agendas].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Get unique months that have events
  const monthsWithEvents = [...new Set(sortedAgendas.map(a => {
    const date = new Date(a.date);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }))].sort();
  
  // Set default to current month if not set
  useEffect(() => {
    if (!selectedMonth && monthsWithEvents.length > 0) {
      const now = new Date();
      const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      // Always set to current month, even if no events exist for it
      setSelectedMonth(currentMonthStr);
    }
  }, [monthsWithEvents, selectedMonth]);
  
  // Filter agendas based on selection
  let filteredAgendas = sortedAgendas;
  if (!viewAll && selectedMonth) {
    filteredAgendas = sortedAgendas.filter(a => {
      const date = new Date(a.date);
      const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      return monthStr === selectedMonth;
    });
  }
  
  // Group by month for display
  const groupedAgendas = {};
  filteredAgendas.forEach(agenda => {
    const date = new Date(agenda.date);
    const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (!groupedAgendas[monthYear]) {
      groupedAgendas[monthYear] = [];
    }
    groupedAgendas[monthYear].push(agenda);
  });
  
  const changeMonth = (direction) => {
    if (!selectedMonth) return;
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1);
    date.setMonth(date.getMonth() + direction);
    const newMonthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(newMonthStr);
    setViewAll(false);
  };
  
  return (
    <div className="list-view-container">
      {/* Month Filter Controls */}
      <div className="month-filter" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        marginBottom: '20px',
        padding: '10px',
        background: '#f5f5f5',
        borderRadius: '5px'
      }}>
        <button 
          className="btn btn-secondary"
          onClick={() => changeMonth(-1)}
          disabled={viewAll}
        >
          ←
        </button>
        
        <select 
          value={viewAll ? 'all' : selectedMonth || ''}
          onChange={(e) => {
            if (e.target.value === 'all') {
              setViewAll(true);
            } else {
              setViewAll(false);
              setSelectedMonth(e.target.value);
            }
          }}
          style={{padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd'}}
        >
          <option value="all">View All Months</option>
          {monthsWithEvents.map(month => {
            const [year, monthNum] = month.split('-');
            const date = new Date(year, monthNum - 1);
            const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            return <option key={month} value={month}>{label}</option>;
          })}
        </select>
        
        <button 
          className="btn btn-secondary"
          onClick={() => changeMonth(1)}
          disabled={viewAll}
        >
          →
        </button>
      </div>
      
      {/* Events List */}
      {Object.keys(groupedAgendas).length > 0 ? (
        Object.entries(groupedAgendas).map(([monthYear, monthAgendas]) => (
          <div key={monthYear} className="month-group">
            <h2 style={{color: '#c45b1f', marginTop: '30px', marginBottom: '15px'}}>
              {monthYear}
            </h2>
            {monthAgendas.map(agenda => (
              <div key={agenda.id} className="agenda-item">
                <div className="agenda-date">
                  {new Date(agenda.date).toLocaleDateString()} at {agenda.time}
                  {agenda.type === 'agenda' && ' - Village Meeting'}
                </div>
                <div className="agenda-title">
                  {agenda.pdf_url ? (
                    <a href={`${API_BASE}${agenda.pdf_url}`} target="_blank" rel="noopener noreferrer">
                      {agenda.title}
                    </a>
                  ) : (
                    agenda.title
                  )}
                </div>
                {agenda.description && (
                  <div className="agenda-description">{agenda.description}</div>
                )}
                {agenda.location && (
                  <div className="agenda-location"><strong>Location:</strong> {agenda.location}</div>
                )}
                {agenda.weather_dependent && (
                  <div className="weather-dependent">☀️ Weather Dependent Event</div>
                )}
                {isAdmin && editingId !== agenda.id && (
                  <div style={{marginTop: '10px', display: 'flex', gap: '10px'}}>
                    <button 
                      className="btn btn-primary"
                      onClick={() => {
                        setEditingId(agenda.id);
                        setEditForm({
                          id: agenda.id,
                          type: agenda.type || 'agenda',
                          date: agenda.date.split('T')[0],
                          time: agenda.time || '7:00 PM',
                          title: agenda.title || '',
                          description: agenda.description || ''
                        });
                      }}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => onDelete(agenda.id)}
                    >
                      Delete
                    </button>
                  </div>
                )}
                {isAdmin && editingId === agenda.id && (
                  <div style={{marginTop: '15px', padding: '15px', background: '#f9f9f9', borderRadius: '5px'}}>
                    <div style={{display: 'grid', gap: '10px'}}>
                      <input
                        type="date"
                        value={editForm.date}
                        onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                        style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}}
                      />
                      <input
                        type="text"
                        value={editForm.time}
                        onChange={(e) => setEditForm({...editForm, time: e.target.value})}
                        placeholder="Time (e.g., 7:00 PM)"
                        style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}}
                      />
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                        placeholder="Description (optional)"
                        rows="3"
                        style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}}
                      />
                      <div style={{display: 'flex', gap: '10px'}}>
                        <button
                          className="btn btn-success"
                          onClick={async () => {
                            const formData = new FormData();
                            formData.append('type', editForm.type);
                            formData.append('date', editForm.date);
                            formData.append('time', editForm.time);
                            formData.append('title', editForm.title);
                            formData.append('description', editForm.description);
                            
                            try {
                              const response = await fetch(`${API_BASE}/api/admin/events/${editForm.id}`, {
                                method: 'PUT',
                                headers: {
                                  'Authorization': `Basic ${window.authToken}`
                                },
                                body: formData
                              });
                              
                              if (response.ok) {
                                alert('Event updated successfully!');
                                setEditingId(null);
                                window.location.reload();
                              }
                            } catch (error) {
                              alert('Failed to update event');
                            }
                          }}
                        >
                          Save Changes
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))
      ) : (
        <p>No events scheduled for this period.</p>
      )}
    </div>
  );
}

// Calendar View Component - Interactive when admin
function CalendarView({ agendas, isAdmin, authToken, onAgendaUpdate }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedViewEvent, setSelectedViewEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    type: 'agenda',
    title: '',
    time: '7:00 PM',
    end_time: '',
    location: '',
    description: '',
    weather_dependent: false,
    pdf: null,
    photos: []
  });
  const [weatherForecast, setWeatherForecast] = useState(null);
  
  const monthYear = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };
  
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };
  
  const getAgendasForDay = (day) => {
    return agendas.filter(a => {
      const date = new Date(a.date);
      return date.getDate() === day && 
             date.getMonth() === currentMonth.getMonth() && 
             date.getFullYear() === currentMonth.getFullYear();
    });
  };
  
  const handleEditEvent = (agenda) => {
    const eventDate = new Date(agenda.date);
    const dateStr = eventDate.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    setSelectedEvent(agenda);
    
    // Pre-fill form with existing values
    setEventForm({
      type: agenda.type || 'agenda',
      title: agenda.title || '',
      time: agenda.time || '7:00 PM',
      end_time: agenda.end_time || '',
      location: agenda.location || '',
      description: agenda.description || '',
      weather_dependent: agenda.weather_dependent || false,
      pdf: null,
      photos: [],
      existing_pdf_url: agenda.pdf_url || null,
      existing_photos: agenda.photo_urls || [],
      remove_pdf: false,
      remove_photo_ids: []
    });
    
    setShowEventModal(true);
  };

  const handleDayClick = async (day) => {
    if (!isAdmin) return;
    
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateStr = clickedDate.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    
    const existingEvents = getAgendasForDay(day);
    
    if (existingEvents.length > 0) {
      // Show existing event for editing
      setSelectedEvent(existingEvents[0]);
      setEventForm({
        type: existingEvents[0].type || 'agenda',
        title: existingEvents[0].title || '',
        time: existingEvents[0].time || '7:00 PM',
        end_time: existingEvents[0].end_time || '',
        location: existingEvents[0].location || '',
        description: existingEvents[0].description || '',
        weather_dependent: existingEvents[0].weather_dependent || false,
        pdf: null,
        photos: []
      });
    } else {
      // New event
      setSelectedEvent(null);
      setEventForm({
        type: 'agenda',
        title: '',
        time: '7:00 PM',
        end_time: '',
        location: '',
        description: '',
        weather_dependent: false,
        pdf: null,
        photos: []
      });
    }
    
    // Fetch weather if date is within 15 days
    const today = new Date();
    const daysDiff = Math.ceil((clickedDate - today) / (1000 * 60 * 60 * 24));
    if (daysDiff >= 0 && daysDiff <= 15) {
      try {
        const response = await axios.get(`${API_BASE}/api/weather`);
        if (response.data.forecast) {
          const forecast = response.data.forecast.find(f => f.date === dateStr);
          setWeatherForecast(forecast);
        }
      } catch (error) {
        console.error('Failed to fetch weather:', error);
      }
    } else {
      setWeatherForecast(null);
    }
    
    setShowEventModal(true);
  };
  
  const handleEventDelete = async () => {
    if (!selectedEvent) return;
    
    if (!confirm('Are you sure you want to delete this event?')) {
      return;
    }
    
    try {
      await axios.delete(
        `${API_BASE}/api/admin/events/${selectedEvent.id}`,
        {
          headers: { 
            'Authorization': `Basic ${authToken}`
          }
        }
      );
      
      // Refresh the events list
      onAgendaUpdate();
      setShowEventModal(false);
      alert('Event deleted successfully!');
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('Failed to delete event. Please try again.');
    }
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData();
    formData.append('type', eventForm.type);
    formData.append('date', selectedDate);
    formData.append('time', eventForm.time);
    formData.append('weather_dependent', eventForm.weather_dependent);
    formData.append('description', eventForm.description);
    
    if (eventForm.type === 'other') {
      formData.append('title', eventForm.title);
      formData.append('location', eventForm.location || '');
      formData.append('end_time', eventForm.end_time || '');
      
      // Add new photos with captions
      if (eventForm.photos && eventForm.photos.length > 0) {
        for (let photo of eventForm.photos) {
          formData.append('photos', photo.file);
        }
      }
      
      // Add photos to remove
      if (eventForm.remove_photo_ids && eventForm.remove_photo_ids.length > 0) {
        formData.append('remove_photos', JSON.stringify(eventForm.remove_photo_ids));
      }
    }
    
    if (eventForm.type === 'agenda') {
      // Handle PDF changes
      if (eventForm.remove_pdf) {
        formData.append('remove_pdf', 'true');
      }
      if (eventForm.pdf) {
        formData.append('pdf', eventForm.pdf);
      }
    }
    
    try {
      if (selectedEvent) {
        // Update existing
        await axios.put(
          `${API_BASE}/api/admin/events/${selectedEvent.id}`,
          formData,
          {
            headers: { 
              'Authorization': `Basic ${authToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        // Create new
        await axios.post(
          `${API_BASE}/api/admin/events`,
          formData,
          {
            headers: { 
              'Authorization': `Basic ${authToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      
      onAgendaUpdate();
      setShowEventModal(false);
      alert('Event saved successfully');
    } catch (error) {
      alert('Failed to save event');
    }
  };
  
  
  const getWeatherIcon = (condition) => {
    const cond = (condition || '').toLowerCase();
    if (cond.includes('sun') || cond.includes('clear')) return 'wi-day-sunny';
    else if (cond.includes('partly')) return 'wi-day-cloudy';
    else if (cond.includes('cloud') || cond.includes('overcast')) return 'wi-cloudy';
    else if (cond.includes('rain') || cond.includes('shower')) return 'wi-rain';
    else if (cond.includes('storm') || cond.includes('thunder')) return 'wi-thunderstorm';
    else if (cond.includes('snow')) return 'wi-snow';
    else if (cond.includes('fog') || cond.includes('mist')) return 'wi-fog';
    return 'wi-cloud';
  };
  
  // Show event details if a non-agenda event was clicked
  if (selectedViewEvent) {
    return (
      <div className="event-detail-view">
        <button 
          className="btn btn-secondary" 
          onClick={() => setSelectedViewEvent(null)}
          style={{marginBottom: '20px'}}
        >
          ← Back to Calendar
        </button>
        
        <h2>{selectedViewEvent.title}</h2>
        
        <div className="event-details">
          <p><strong>Date:</strong> {new Date(selectedViewEvent.date).toLocaleDateString()}</p>
          <p><strong>Time:</strong> {selectedViewEvent.time}{selectedViewEvent.end_time ? ` - ${selectedViewEvent.end_time}` : ''}</p>
          {selectedViewEvent.location && <p><strong>Location:</strong> {selectedViewEvent.location}</p>}
          
          {selectedViewEvent.weather_dependent && (
            <div className="weather-notice">
              <i className="wi wi-day-rain"></i> This is an outdoor event (weather dependent)
            </div>
          )}
          
          {selectedViewEvent.description && (
            <div className="event-description">
              <h3>Description</h3>
              <p>{selectedViewEvent.description}</p>
            </div>
          )}
          
          {selectedViewEvent.photo_urls && selectedViewEvent.photo_urls.length > 0 && (
            <div className="event-photos">
              <h3>Event Photos</h3>
              <div className="photo-grid">
                {selectedViewEvent.photo_urls.map((url, index) => (
                  <img 
                    key={index} 
                    src={`${API_BASE}${url}`} 
                    alt={`Event photo ${index + 1}`}
                    style={{width: '200px', margin: '10px'}}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button className="btn btn-secondary" onClick={prevMonth}>←</button>
        <h3>{monthYear}</h3>
        <button className="btn btn-secondary" onClick={nextMonth}>→</button>
      </div>
      
      {/* Legend */}
      <div className="calendar-legend" style={{
        display: 'flex',
        gap: '20px',
        justifyContent: 'center',
        marginBottom: '10px',
        fontSize: '0.85rem'
      }}>
        <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
          <span style={{
            display: 'inline-block',
            width: '20px',
            height: '12px',
            backgroundColor: '#c45b1f',
            borderRadius: '2px'
          }}></span>
          <span>Agenda</span>
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
          <span style={{
            display: 'inline-block',
            width: '20px',
            height: '12px',
            backgroundColor: '#315331',
            borderRadius: '2px'
          }}></span>
          <span>Event</span>
        </div>
      </div>
      <div className="calendar-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="calendar-day-header">{day}</div>
        ))}
        {Array(firstDay).fill(null).map((_, i) => (
          <div key={`empty-${i}`} className="calendar-day"></div>
        ))}
        {Array(daysInMonth).fill(null).map((_, i) => {
          const day = i + 1;
          const dayAgendas = getAgendasForDay(day);
          const hasAgenda = dayAgendas.some(e => e.type === 'agenda');
          const hasOutdoorEvent = dayAgendas.some(e => e.weather_dependent);
          
          return (
            <div 
              key={day} 
              className={`calendar-day ${isAdmin ? 'admin-clickable' : ''} ${dayAgendas.length > 0 ? 'has-events' : ''}`}
              onClick={() => handleDayClick(day)}
              title={isAdmin ? 'Click to add/edit event' : ''}
            >
              <div style={{fontWeight: 'bold', marginBottom: '5px'}}>{day}</div>
              {dayAgendas.map(agenda => {
                // Simplify display for calendar view
                const isAgenda = agenda.type === 'agenda';
                let displayText = '';
                
                if (isAgenda) {
                  // For agendas, just show "Agenda M/D" 
                  const eventDate = new Date(agenda.date);
                  const month = eventDate.getMonth() + 1;
                  const dayNum = eventDate.getDate();
                  displayText = `Agenda ${month}/${dayNum}`;
                } else {
                  // For other events, show abbreviated title
                  displayText = agenda.title.length > 12 
                    ? agenda.title.substring(0, 12) + '...' 
                    : agenda.title;
                }
                
                return (
                  <div 
                    key={agenda.id} 
                    className={`calendar-event ${isAgenda ? 'agenda-event' : 'community-event'} ${isAgenda && agenda.pdf_url ? 'has-pdf' : ''}`}
                    title={`${agenda.title} - ${agenda.time}${isAgenda && agenda.pdf_url ? ' (PDF Available)' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation(); // Don't trigger day click
                      if (!isAdmin) {
                        // Regular users can click to view
                        if (isAgenda && agenda.pdf_url) {
                          // Open PDF in new tab
                          window.open(`${API_BASE}${agenda.pdf_url}`, '_blank');
                        } else if (isAgenda && !agenda.pdf_url) {
                          // Show no PDF message
                          alert('No PDF has been uploaded for this agenda yet.');
                        } else if (!isAgenda) {
                          // Show event details
                          setSelectedViewEvent(agenda);
                        }
                      } else {
                        // Admin mode - edit the event
                        handleEditEvent(agenda);
                      }
                    }}
                  >
                    <div style={{fontSize: '0.65rem', lineHeight: '1.2'}}>
                      {displayText}
                      {agenda.weather_dependent && ' ☀️'}
                    </div>
                  </div>
                );
              })}
              {isAdmin && dayAgendas.length === 0 && (
                <div className="calendar-add-hint">+</div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Event Modal for Admin */}
      {showEventModal && isAdmin && (
        <div className="modal active">
          <div className="modal-content" style={{maxWidth: '700px'}}>
            <div className="modal-header">
              <h2>
                {selectedEvent ? 'Edit Event' : 'Add Event'} - {new Date(selectedDate).toLocaleDateString()}
              </h2>
              <button className="modal-close" onClick={() => setShowEventModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleEventSubmit}>
              {/* Date Selection (for editing) */}
              <div className="form-group">
                <label>Event Date:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  required
                  style={{marginLeft: '10px'}}
                />
              </div>
              
              {/* Event Type Selection */}
              <div className="form-group">
                <label>Event Type:</label>
                <div style={{display: 'flex', gap: '30px', marginTop: '10px'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap'}}>
                    <input
                      type="radio"
                      value="agenda"
                      checked={eventForm.type === 'agenda'}
                      onChange={(e) => setEventForm({...eventForm, type: e.target.value})}
                    />
                    <span>Village Agenda</span>
                  </label>
                  <label style={{display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap'}}>
                    <input
                      type="radio"
                      value="other"
                      checked={eventForm.type === 'other'}
                      onChange={(e) => setEventForm({...eventForm, type: e.target.value})}  
                    />
                    <span>Community Event</span>
                  </label>
                </div>
                {eventForm.type === 'agenda' && (
                  <small style={{color: '#666', marginTop: '5px', display: 'block'}}>
                    Title will be auto-generated as "Agenda: {new Date(selectedDate).toLocaleDateString()}"
                  </small>
                )}
              </div>
              
              {/* Title (only for non-agenda events) */}
              {eventForm.type === 'other' && (
                <div className="form-group">
                  <label>Event Title:</label>
                  <input
                    type="text"
                    value={eventForm.title}
                    onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                    placeholder="e.g., Haunted Hayride"
                    required
                  />
                </div>
              )}
              
              {/* Time */}
              <div className="form-group">
                <label>Start Time:</label>
                <input
                  type="time"
                  defaultValue="19:00"
                  onChange={(e) => {
                    const time = e.target.value;
                    const [hours, minutes] = time.split(':');
                    const hour = parseInt(hours);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                    setEventForm({...eventForm, time: `${displayHour}:${minutes} ${ampm}`});
                  }}
                />
              </div>
              
              {/* End Time (for events) */}
              {eventForm.type === 'other' && (
                <div className="form-group">
                  <label>End Time (optional):</label>
                  <input
                    type="time"
                    onChange={(e) => {
                      if (e.target.value) {
                        const time = e.target.value;
                        const [hours, minutes] = time.split(':');
                        const hour = parseInt(hours);
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                        setEventForm({...eventForm, end_time: `${displayHour}:${minutes} ${ampm}`});
                      }
                    }}
                  />
                </div>
              )}
              
              {/* Location (for events) */}
              {eventForm.type === 'other' && (
                <div className="form-group">
                  <label>Location:</label>
                  <input
                    type="text"
                    value={eventForm.location}
                    onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                    placeholder="e.g., Village Park"
                  />
                </div>
              )}
              
              {/* Weather Dependent (for events) */}
              {eventForm.type === 'other' && (
                <div className="form-group">
                  <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <input
                      type="checkbox"
                      checked={eventForm.weather_dependent}
                      onChange={(e) => setEventForm({...eventForm, weather_dependent: e.target.checked})}
                    />
                    <span>This is an outdoor event (weather dependent)</span>
                  </label>
                  
                  {eventForm.weather_dependent && weatherForecast && (
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      background: '#e8f4f8',
                      borderRadius: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <i className={`wi ${getWeatherIcon(weatherForecast.condition)}`} style={{fontSize: '24px'}}></i>
                      <span>{weatherForecast.condition}</span>
                      <span>{weatherForecast.maxtemp_F}°/{weatherForecast.mintemp_F}°F</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Description */}
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                  placeholder="Event details..."
                  rows="3"
                />
              </div>
              
              {/* PDF Upload (for agendas) */}
              {eventForm.type === 'agenda' && (
                <div className="form-group">
                  <label>Agenda PDF:</label>
                  {eventForm.existing_pdf_url && !eventForm.remove_pdf ? (
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px'}}>
                      <a href={`${API_BASE}${eventForm.existing_pdf_url}`} target="_blank" rel="noopener noreferrer" style={{color: '#c45b1f'}}>
                        📄 View current PDF
                      </a>
                      <button 
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => setEventForm({...eventForm, remove_pdf: true})}
                      >
                        Remove PDF
                      </button>
                      <button 
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => document.getElementById('pdf-replace').click()}
                      >
                        Replace PDF
                      </button>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setEventForm({...eventForm, pdf: e.target.files[0], remove_pdf: false})}
                      />
                      {eventForm.remove_pdf && (
                        <button 
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setEventForm({...eventForm, remove_pdf: false})}
                          style={{marginTop: '5px'}}
                        >
                          Cancel Remove
                        </button>
                      )}
                    </div>
                  )}
                  <input
                    id="pdf-replace"
                    type="file"
                    accept=".pdf"
                    style={{display: 'none'}}
                    onChange={(e) => setEventForm({...eventForm, pdf: e.target.files[0], remove_pdf: false})}
                  />
                </div>
              )}
              
              {/* Photo Upload (for community events) */}
              {eventForm.type === 'other' && (
                <div className="form-group">
                  <label>Event Photos:</label>
                  
                  {/* Existing photos */}
                  {eventForm.existing_photos && eventForm.existing_photos.length > 0 && (
                    <div style={{marginBottom: '10px'}}>
                      <p style={{fontSize: '0.9em', color: '#666'}}>Existing photos:</p>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: '10px',
                        marginBottom: '10px'
                      }}>
                        {eventForm.existing_photos.filter(url => !eventForm.remove_photo_ids?.includes(url)).map((url, index) => (
                          <div key={`existing-${index}`} style={{
                            position: 'relative',
                            border: '2px solid #c45b1f',
                            borderRadius: '5px',
                            padding: '5px'
                          }}>
                            <img 
                              src={`${API_BASE}${url}`} 
                              alt={`Existing ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '100px',
                                objectFit: 'cover',
                                borderRadius: '3px'
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                padding: '2px 6px'
                              }}
                              onClick={() => {
                                const removed = [...(eventForm.remove_photo_ids || []), url];
                                setEventForm({...eventForm, remove_photo_ids: removed});
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      const newPhotos = files.map(file => ({
                        file,
                        caption: '',
                        preview: URL.createObjectURL(file)
                      }));
                      setEventForm({...eventForm, photos: [...(eventForm.photos || []), ...newPhotos]});
                    }}
                  />
                  
                  {eventForm.photos && eventForm.photos.length > 0 && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                      gap: '10px',
                      marginTop: '10px'
                    }}>
                      {eventForm.photos.map((photo, index) => (
                        <div key={index} style={{
                          position: 'relative',
                          border: '1px solid #ddd',
                          borderRadius: '5px',
                          padding: '5px'
                        }}>
                          <img 
                            src={photo.preview} 
                            alt={`Preview ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100px',
                              objectFit: 'cover',
                              borderRadius: '3px'
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Caption..."
                            value={photo.caption}
                            onChange={(e) => {
                              const updatedPhotos = [...eventForm.photos];
                              updatedPhotos[index].caption = e.target.value;
                              setEventForm({...eventForm, photos: updatedPhotos});
                            }}
                            style={{
                              width: '100%',
                              marginTop: '5px',
                              padding: '3px',
                              fontSize: '12px'
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const updatedPhotos = eventForm.photos.filter((_, i) => i !== index);
                              setEventForm({...eventForm, photos: updatedPhotos});
                            }}
                            style={{
                              position: 'absolute',
                              top: '2px',
                              right: '2px',
                              background: '#e53e3e',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              lineHeight: '1'
                            }}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {selectedEvent?.photo_urls && selectedEvent.photo_urls.length > 0 && (
                    <div style={{marginTop: '10px'}}>
                      <small style={{color: '#666'}}>Existing photos: {selectedEvent.photo_urls.length}</small>
                    </div>
                  )}
                </div>
              )}
              
              {/* Action Buttons */}
              <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
                <button type="submit" className="btn btn-primary">
                  {selectedEvent ? 'Update Event' : 'Create Event'}
                </button>
                
                {selectedEvent && (
                  <button type="button" className="btn btn-danger" onClick={handleEventDelete}>
                    Delete Event
                  </button>
                )}
                
                <button type="button" className="btn btn-secondary" onClick={() => setShowEventModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Simple Admin Login Modal
function AdminLoginModal({ onLogin, onClose }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (await onLogin(username, password)) {
      onClose();
    }
  };

  return (
    <div className="modal active">
      <div className="modal-content" style={{maxWidth: '400px'}}>
        <div className="modal-header">
          <h2>Admin Login</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} autoComplete="on">
          <div className="form-group">
            <label>Username:</label>
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              required
            />
          </div>
          <div className="form-group">
            <label>Password:</label>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
            />
          </div>
          <button type="submit" className="btn btn-primary">Login</button>
        </form>
      </div>
    </div>
  );
}

// Old Admin Modal Component (kept for reference but not used)
function AdminModal({ isAdmin, onLogin, onClose, onPhotoUpload, onAgendaCreate, photos, onPhotoDelete }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [agendaForm, setAgendaForm] = useState({
    title: '',
    date: '',
    time: '',
    description: '',
    link: ''
  });
  
  const handleLogin = async () => {
    if (await onLogin(username, password)) {
      setUsername('');
      setPassword('');
    }
  };
  
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const caption = prompt('Enter photo caption (optional):');
      onPhotoUpload(file, caption || '');
    }
  };
  
  const handleAgendaSubmit = () => {
    if (!agendaForm.title || !agendaForm.date || !agendaForm.time) {
      alert('Please fill in required fields');
      return;
    }
    onAgendaCreate(agendaForm);
    setAgendaForm({
      title: '',
      date: '',
      time: '',
      description: '',
      link: ''
    });
  };
  
  return (
    <div className="modal active">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Admin Panel</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        {!isAdmin ? (
          <div>
            <div className="form-group">
              <label>Username:</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
              />
            </div>
            <div className="form-group">
              <label>Password:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
              />
            </div>
            <button className="btn btn-primary" onClick={handleLogin}>Login</button>
          </div>
        ) : (
          <div>
            <h3>Manage Photos</h3>
            <div className="photo-upload-area">
              <input type="file" accept="image/*" onChange={handlePhotoUpload} />
              <p>Upload photos for the homepage slideshow</p>
            </div>
            <div className="photo-grid">
              {photos.map(photo => (
                <div key={photo.id} className="photo-item">
                  <img src={`${API_BASE}/uploads/${photo.filename}`} alt={photo.caption} />
                  <button className="photo-delete" onClick={() => onPhotoDelete(photo.id)}>×</button>
                </div>
              ))}
            </div>
            
            <hr />
            
            <h3>Add New Agenda</h3>
            <div className="form-group">
              <label>Title:</label>
              <input
                type="text"
                value={agendaForm.title}
                onChange={(e) => setAgendaForm({...agendaForm, title: e.target.value})}
                placeholder="e.g., Village Board Meeting"
              />
            </div>
            <div className="form-group">
              <label>Date:</label>
              <input
                type="date"
                value={agendaForm.date}
                onChange={(e) => setAgendaForm({...agendaForm, date: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Time:</label>
              <input
                type="time"
                value={agendaForm.time}
                onChange={(e) => setAgendaForm({...agendaForm, time: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Description (optional):</label>
              <textarea
                value={agendaForm.description}
                onChange={(e) => setAgendaForm({...agendaForm, description: e.target.value})}
                placeholder="Meeting details..."
              />
            </div>
            <div className="form-group">
              <label>Link (optional):</label>
              <input
                type="url"
                value={agendaForm.link}
                onChange={(e) => setAgendaForm({...agendaForm, link: e.target.value})}
                placeholder="https://example.com/agenda.pdf"
              />
            </div>
            <button className="btn btn-primary" onClick={handleAgendaSubmit}>Add Agenda</button>
          </div>
        )}
      </div>
    </div>
  );
}

// Markdown Editor Component with Tabs and Toolbar
function MarkdownEditor({ title, content, onTitleChange, onContentChange, onSave, authToken }) {
  const [activeTab, setActiveTab] = useState('edit');
  const [isUploading, setIsUploading] = useState(false);
  const textareaRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  
  
  // Insert markdown formatting
  const insertFormatting = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    const replacement = before + selectedText + after;
    
    const newContent = content.substring(0, start) + replacement + content.substring(end);
    onContentChange(newContent);
    
    // Reset cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };
  
  const insertAtCursor = (text) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const newContent = content.substring(0, start) + text + content.substring(start);
    onContentChange(newContent);
    
    setTimeout(() => {
      textarea.focus();
      const newPos = start + text.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Check file size (100MB limit)
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    if (file.size > maxSize) {
      alert(`File too large! Maximum size is 100MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }
    
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_BASE}/api/admin/upload`, formData, {
        headers: {
          'Authorization': `Basic ${authToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data && response.data.url) {
        const fileUrl = response.data.url;
        const fileName = file.name;
        
        // Check file type
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
        const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi'];
        const fileExtension = fileName.split('.').pop().toLowerCase();
        const isImage = imageExtensions.includes(fileExtension);
        const isVideo = videoExtensions.includes(fileExtension);
        
        if (isImage) {
          // Insert image markdown - it will display inline
          insertAtCursor(`\n![${fileName}](${fileUrl})\n\n`);
        } else if (isVideo) {
          // Insert HTML video tag for inline video display
          insertAtCursor(`\n<video controls width="100%" style="max-width: 600px;">\n  <source src="${fileUrl}" type="video/${fileExtension}">\n  Your browser does not support the video tag.\n</video>\n\n`);
        } else {
          // Insert link markdown for PDFs and other files
          // Intelligently name the link based on file type
          const isPDF = fileExtension === 'pdf';
          const linkName = isPDF ? `📄 ${fileName}` : fileName;
          const linkText = `[${linkName}](${fileUrl})`;
          
          const textarea = textareaRef.current;
          const start = textarea.selectionStart;
          insertAtCursor(linkText);
          
          // If it's a PDF, select just the filename part for easy editing
          if (isPDF) {
            setTimeout(() => {
              textarea.focus();
              const descStart = start + 4; // Skip past "[📄 "
              const descEnd = start + linkName.length; // Select just filename
              textarea.setSelectionRange(descStart, descEnd);
            }, 50);
          }
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  return (
    <div className="markdown-editor" style={{background: 'transparent'}}>
      {title !== undefined && (
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          onBlur={onSave}
          style={{fontSize: '1.5rem', color: '#c45b1f', width: '100%', marginBottom: '10px', padding: '5px', border: '1px dashed #c45b1f', background: 'transparent'}}
          placeholder="Enter title..."
        />
      )}
      
      {/* Tab Buttons */}
      <div className="editor-tabs" style={{
        display: 'flex',
        borderBottom: '2px solid #ddd',
        marginBottom: '0'
      }}>
        <button
          onClick={() => setActiveTab('edit')}
          style={{
            padding: '8px 16px',
            background: activeTab === 'edit' ? '#fff' : '#f5f5f5',
            border: '1px solid #ddd',
            borderBottom: activeTab === 'edit' ? '2px solid #fff' : 'none',
            marginBottom: activeTab === 'edit' ? '-2px' : '0',
            cursor: 'pointer',
            fontWeight: activeTab === 'edit' ? 'bold' : 'normal'
          }}
        >
          Edit
        </button>
        <button
          onClick={() => setActiveTab('preview')}
          style={{
            padding: '8px 16px',
            background: activeTab === 'preview' ? '#fff' : '#f5f5f5',
            border: '1px solid #ddd',
            borderBottom: activeTab === 'preview' ? '2px solid #fff' : 'none',
            marginBottom: activeTab === 'preview' ? '-2px' : '0',
            cursor: 'pointer',
            fontWeight: activeTab === 'preview' ? 'bold' : 'normal'
          }}
        >
          Preview
        </button>
      </div>
      
      {activeTab === 'edit' ? (
        <div>
          {/* Toolbar */}
          <div className="editor-toolbar" style={{
            padding: '6px 8px',
            background: 'linear-gradient(to bottom, #ffffff, #f8f8f8)',
            borderLeft: '1px solid #ddd',
            borderRight: '1px solid #ddd',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            gap: '2px',
            alignItems: 'center',
            flexWrap: 'nowrap',
            overflowX: 'auto'
          }}>
            <button
              type="button"
              onClick={() => insertFormatting('**', '**')}
              className="toolbar-btn"
              style={{
                padding: '5px 10px',
                background: 'transparent',
                border: '1px solid transparent',
                borderRadius: '3px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
                minWidth: '30px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#e8e8e8';
                e.target.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              title="Bold"
            >
              B
            </button>
            
            <button
              type="button"
              onClick={() => insertFormatting('*', '*')}
              style={{
                padding: '5px 10px',
                background: 'transparent',
                border: '1px solid transparent',
                borderRadius: '3px',
                cursor: 'pointer',
                fontStyle: 'italic',
                fontSize: '14px',
                minWidth: '30px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#e8e8e8';
                e.target.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              title="Italic"
            >
              I
            </button>
            
            <div style={{width: '1px', height: '20px', background: '#ddd', margin: '0 4px'}}></div>
            
            <button
              type="button"
              onClick={() => insertFormatting('## ')}
              style={{
                padding: '5px 8px',
                background: 'transparent',
                border: '1px solid transparent',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '13px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#e8e8e8';
                e.target.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              title="Heading"
            >
              H2
            </button>
            
            <button
              type="button"
              onClick={() => insertFormatting('### ')}
              style={{
                padding: '5px 8px',
                background: 'transparent',
                border: '1px solid transparent',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '13px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#e8e8e8';
                e.target.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              title="Subheading"
            >
              H3
            </button>
            
            <div style={{width: '1px', height: '20px', background: '#ddd', margin: '0 4px'}}></div>
            
            <button
              type="button"
              onClick={() => insertAtCursor('\n- ')}
              style={{
                padding: '5px 8px',
                background: 'transparent',
                border: '1px solid transparent',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#e8e8e8';
                e.target.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              title="Bullet List"
            >
              •
            </button>
            
            <button
              type="button"
              onClick={() => insertAtCursor('\n1. ')}
              style={{
                padding: '5px 8px',
                background: 'transparent',
                border: '1px solid transparent',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '13px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#e8e8e8';
                e.target.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              title="Numbered List"
            >
              1.
            </button>
            
            <div style={{width: '1px', height: '20px', background: '#ddd', margin: '0 4px'}}></div>
            
            <button
              type="button"
              onClick={() => insertFormatting('[', '](url)')}
              style={{
                padding: '5px 8px',
                background: 'transparent',
                border: '1px solid transparent',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#e8e8e8';
                e.target.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              title="Insert Link"
            >
              🔗
            </button>
            
            <button
              type="button"
              onClick={() => insertFormatting('`', '`')}
              style={{
                padding: '5px 8px',
                background: 'transparent',
                border: '1px solid transparent',
                borderRadius: '3px',
                cursor: 'pointer',
                fontFamily: 'monospace',
                fontSize: '13px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#e8e8e8';
                e.target.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              title="Code"
            >
              &lt;/&gt;
            </button>
            
            <button
              type="button"
              onClick={() => {
                const textarea = textareaRef.current;
                if (!textarea) return;
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const selectedText = content.substring(start, end);
                
                if (selectedText) {
                  // Quote selected text on new lines
                  const quotedText = selectedText.split('\n').map(line => `> ${line}`).join('\n');
                  const newContent = content.substring(0, start) + quotedText + content.substring(end);
                  onContentChange(newContent);
                } else {
                  // Just insert quote marker
                  insertAtCursor('\n> ');
                }
              }}
              style={{
                padding: '5px 8px',
                background: 'transparent',
                border: '1px solid transparent',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#e8e8e8';
                e.target.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              title="Quote"
            >
              "
            </button>
            
            <button
              type="button"
              onClick={() => insertAtCursor('\n---\n')}
              style={{
                padding: '5px 10px',
                background: 'transparent',
                border: '1px solid transparent',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#e8e8e8';
                e.target.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              title="Horizontal Rule"
            >
              ―
            </button>
            
            <div style={{width: '1px', height: '20px', background: '#ddd', margin: '0 4px'}}></div>
            
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                padding: '5px 10px',
                background: isUploading ? '#c45b1f' : 'transparent',
                border: `1px solid ${isUploading ? '#c45b1f' : 'transparent'}`,
                borderRadius: '3px',
                cursor: isUploading ? 'wait' : 'pointer',
                color: isUploading ? '#fff' : '#333',
                fontSize: '13px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onMouseEnter={(e) => {
                if (!isUploading) {
                  e.target.style.background = '#e8e8e8';
                  e.target.style.borderColor = '#ccc';
                }
              }}
              onMouseLeave={(e) => {
                if (!isUploading) {
                  e.target.style.background = 'transparent';
                  e.target.style.borderColor = 'transparent';
                }
              }}
              title="Upload File (max 100MB)"
              disabled={isUploading}
            >
              {isUploading ? '⏳' : '📎'} {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            style={{display: 'none'}}
            onChange={handleFileUpload}
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          />
          
          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => onContentChange(e.target.value)}
            onBlur={(e) => onSave && onSave(e.target.value)}
            style={{
              width: '100%',
              minHeight: '300px',
              lineHeight: '1.6',
              padding: '10px',
              border: '1px solid #ddd',
              borderTop: 'none',
              fontFamily: 'monospace',
              fontSize: '14px'
            }}
            placeholder="Enter content in Markdown...

**Bold text** with double asterisks
*Italic text* with single asterisks
## Headings with hash symbols
- Bullet lists with dashes
1. Numbered lists

Double enter for new paragraph..."
          />
        </div>
      ) : (
        <div style={{
          border: '1px solid #ddd',
          borderTop: 'none',
          padding: '20px',
          minHeight: '300px',
          background: 'transparent'
        }}>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{content || '*No content yet...*'}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

// Compact Markdown Editor for Footer
function CompactMarkdownEditor({ value, onChange, onBlur, placeholder, rows = 3 }) {
  const [isPreview, setIsPreview] = useState(false);
  
  return (
    <div style={{position: 'relative', marginTop: '5px'}}>
      <div style={{
        position: 'absolute',
        top: '5px',
        right: '5px',
        zIndex: 10,
        display: 'flex',
        gap: '5px'
      }}>
        <button
          type="button"
          onClick={() => setIsPreview(!isPreview)}
          style={{
            padding: '2px 6px',
            fontSize: '11px',
            background: isPreview ? '#315331' : '#c45b1f',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            cursor: 'pointer'
          }}
        >
          {isPreview ? 'Edit' : 'Preview'}
        </button>
      </div>
      
      {isPreview ? (
        <div style={{
          padding: '10px',
          border: '1px dashed #fff',
          background: 'rgba(255,255,255,0.1)',
          minHeight: `${rows * 30}px`,
          borderRadius: '3px',
          color: '#fff'
        }} className="footer-preview">
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{value || '*No content*'}</ReactMarkdown>
        </div>
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          rows={rows}
          style={{
            width: '100%',
            padding: '5px 40px 5px 5px',
            border: '1px dashed #fff',
            background: 'transparent',
            color: '#fff',
            fontSize: '14px',
            fontFamily: 'monospace',
            resize: 'vertical'
          }}
        />
      )}
    </div>
  );
}

// Privacy Policy Component
function PrivacyPolicy() {
  return (
    <div className="static-page">
      <h1>Privacy Policy</h1>
      <p><em>Last Updated: January 2025</em></p>
      
      <h2>Information We Collect</h2>
      <p>
        The Village of Thomasboro website collects minimal information from visitors. We may collect:
      </p>
      <ul>
        <li>Contact information voluntarily provided through forms (name, email, phone number)</li>
        <li>Newsletter subscription information (email address)</li>
        <li>Standard web server logs (IP addresses, browser types, referring pages)</li>
      </ul>
      
      <h2>How We Use Your Information</h2>
      <p>
        Information collected is used solely for Village business purposes:
      </p>
      <ul>
        <li>Responding to inquiries and service requests</li>
        <li>Sending requested newsletters and village updates</li>
        <li>Improving website functionality and user experience</li>
        <li>Complying with Illinois Freedom of Information Act requirements</li>
      </ul>
      
      <h2>Information Sharing</h2>
      <p>
        The Village of Thomasboro does not sell, rent, or share personal information with third parties except:
      </p>
      <ul>
        <li>As required by Illinois state law or federal law</li>
        <li>In response to valid legal processes (subpoenas, court orders)</li>
        <li>To protect public safety or village operations</li>
      </ul>
      
      <h2>Public Records</h2>
      <p>
        Please be aware that certain information submitted to the Village may be subject to disclosure under the Illinois Freedom of Information Act (FOIA). 
        Public records requests are handled according to state law.
      </p>
      
      <h2>Data Security</h2>
      <p>
        We implement reasonable security measures to protect information in our possession. However, no data transmission over the internet 
        is 100% secure. We cannot guarantee absolute security of information transmitted to our website.
      </p>
      
      <h2>Cookies</h2>
      <p>
        This website uses minimal cookies for basic functionality. You may disable cookies in your browser settings, though some features 
        may not function properly.
      </p>
      
      <h2>Children's Privacy</h2>
      <p>
        We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, 
        we will delete it immediately.
      </p>
      
      <h2>Contact Information</h2>
      <p>
        For questions about this privacy policy or to request information about data we may have collected, contact:
      </p>
      <p>
        Village Office<br />
        Village of Thomasboro<br />
        101 W Main Street<br />
        Thomasboro, IL 61878<br />
        Phone: (217) 643-2675<br />
        Email: office@thomasboro.us
      </p>
      
      <h2>Changes to This Policy</h2>
      <p>
        This privacy policy may be updated periodically. Changes will be posted on this page with an updated revision date.
      </p>
    </div>
  );
}

// Accessibility Statement Component
function AccessibilityStatement() {
  return (
    <div className="static-page">
      <h1>Accessibility Statement</h1>
      
      <p>
        The Village of Thomasboro is committed to ensuring digital accessibility for people with disabilities. 
        We are continually improving the user experience for everyone and applying the relevant accessibility standards.
      </p>
      
      <h2>Our Commitment</h2>
      <p>
        We strive to ensure that our website is accessible to people with disabilities. We aim to comply with Web Content 
        Accessibility Guidelines (WCAG) 2.1 Level AA standards where feasible for a village of our size.
      </p>
      
      <h2>Accessibility Features</h2>
      <p>Our website includes the following accessibility features:</p>
      <ul>
        <li>Alternative text for images</li>
        <li>Structured headings for easy navigation</li>
        <li>Sufficient color contrast for text</li>
        <li>Keyboard navigation support</li>
        <li>Clear and simple language</li>
        <li>Consistent navigation throughout the site</li>
      </ul>
      
      <h2>Documents and PDFs</h2>
      <p>
        We are working to ensure all new documents posted to our website are accessible. Older documents may not yet meet 
        accessibility standards. If you need an accessible version of any document, please contact us.
      </p>
      
      <h2>Known Limitations</h2>
      <p>
        Despite our efforts, some content may not be fully accessible:
      </p>
      <ul>
        <li>Some older PDF documents may not be fully accessible</li>
        <li>Third-party content or tools may have accessibility limitations</li>
        <li>Some interactive features may require JavaScript</li>
      </ul>
      
      <h2>Request Accessible Format</h2>
      <p>
        If you need information from our website in a different format, such as large print, braille, or audio, 
        please contact us. We will work with you to provide the information in a format that meets your needs.
      </p>
      
      <h2>Report Accessibility Issues</h2>
      <p>
        We welcome your feedback on the accessibility of our website. If you encounter accessibility barriers, please contact us:
      </p>
      <p>
        Village Office<br />
        Village of Thomasboro<br />
        101 W Main Street<br />
        Thomasboro, IL 61878<br />
        Phone: (217) 643-2675<br />
        Email: office@thomasboro.us
      </p>
      <p>
        Please provide:
      </p>
      <ul>
        <li>Description of the accessibility issue</li>
        <li>Web address (URL) where you encountered the issue</li>
        <li>Your contact information for follow-up</li>
      </ul>
      
      <h2>Response Time</h2>
      <p>
        We aim to respond to accessibility feedback within 5 business days. Due to our small staff size, complex issues 
        may take longer to resolve, but we will keep you informed of our progress.
      </p>
      
      <h2>Enforcement</h2>
      <p>
        If you believe the Village has not provided an accessible service or you wish to file a formal complaint, 
        you may contact:
      </p>
      <p>
        Illinois Attorney General's Office<br />
        Disability Rights Bureau<br />
        100 West Randolph Street<br />
        Chicago, IL 60601<br />
        Phone: (312) 814-3000<br />
        TTY: (800) 964-3013
      </p>
      
      <h2>Assessment Approach</h2>
      <p>
        The Village of Thomasboro assessed the accessibility of this website through self-evaluation and community feedback.
      </p>
    </div>
  );
}

export default App;